import * as React from "react";
import { SlidersHorizontal } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

export interface FilterBarProps {
  /** Filter field controls, laid out in a responsive grid */
  children: React.ReactNode;
  activeFilterCount?: number;
  onClear: () => void;
  onApply: () => void;
  isApplying?: boolean;
  triggerLabel?: string;
  title?: string;
  description?: string;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  gridClassName?: string;
}

export function FilterBar({
  children,
  activeFilterCount = 0,
  onClear,
  onApply,
  isApplying = false,
  triggerLabel = "Filters",
  title = "Filters",
  description,
  open,
  onOpenChange,
  gridClassName,
}: FilterBarProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button variant="outline" className="relative">
          <SlidersHorizontal className="mr-2 h-4 w-4" />
          {triggerLabel}
          {activeFilterCount > 0 && (
            <span className="absolute -right-1 -top-1 h-2 w-2 rounded-full bg-primary" />
          )}
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>
        <div className={cn("grid grid-cols-1 gap-4 sm:grid-cols-2", gridClassName)}>
          {children}
        </div>
        <DialogFooter className="flex justify-between sm:justify-between">
          <Button variant="ghost" onClick={onClear} type="button">
            Clear All
          </Button>
          <div className="flex gap-2">
            <DialogClose asChild>
              <Button variant="outline" type="button">
                Cancel
              </Button>
            </DialogClose>
            <Button onClick={onApply} disabled={isApplying} type="button">
              {isApplying ? "Applying..." : "Apply Filters"}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
